import React from 'react';
import { cn } from '~/lib/utils';

type BadgeVariant = 'default' | 'secondary' | 'outline' | 'info' | 'success' | 'warning' | 'danger';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
}

export function Badge({ variant = 'default', className, ...props }: BadgeProps) {
  return <span className={cn(baseBadgeClass(variant), className)} {...props} />;
}

function baseBadgeClass(variant: BadgeVariant) {
  const variants: Record<BadgeVariant, string> = {
    default: 'bg-primary text-primary-foreground',
    secondary: 'bg-secondary text-secondary-foreground',
    outline: 'border border-border bg-transparent',
    info: 'bg-blue-500/10 text-blue-500',
    success: 'bg-green-500/10 text-green-500',
    warning: 'bg-amber-500/10 text-amber-500',
    danger: 'bg-red-500/10 text-red-500',
  };

  return cn(
    'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide',
    variants[variant]
  );
}

export interface CategoryBadgeProps {
  category: string;
}

export function CategoryBadge({ category }: CategoryBadgeProps) {
  return <span className={baseBadgeClass('info')}>{category}</span>;
}

export interface TagBadgeProps {
  tag: string;
}

export function TagBadge({ tag }: TagBadgeProps) {
  return <span className={baseBadgeClass('default')}>#{tag}</span>;
}

export interface StatusBadgeProps {
  status: 'draft' | 'published' | 'archived';
}

export function StatusBadge({ status }: StatusBadgeProps) {
  const variant: Record<StatusBadgeProps['status'], BadgeVariant> = {
    draft: 'warning',
    published: 'success',
    archived: 'danger',
  };

  return <span className={baseBadgeClass(variant[status])}>{status}</span>;
}

export interface RarityBadgeProps {
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
}

export function RarityBadge({ rarity }: RarityBadgeProps) {
  const colors: Record<RarityBadgeProps['rarity'], string> = {
    common: 'bg-gray-500/10 text-gray-500',
    rare: 'bg-blue-500/10 text-blue-500',
    epic: 'bg-purple-500/10 text-purple-500',
    legendary: 'bg-amber-500/10 text-amber-500',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide',
        colors[rarity]
      )}
    >
      {rarity}
    </span>
  );
}
